import { useMemo } from "react";
import {
  parse,
  parseISO,
  differenceInDays,
  isAfter,
  setHours,
  setMinutes,
  setSeconds,
  isValid,
} from "date-fns";
import { Campaign } from "@/Components/Home/CampaignCard.tsx";

export type useCampaignDeadlineProps = {
  registrationEnd: string;
  expirationInDays: Campaign['expired_in_days']
}

const useCampaignDeadline = ({ registrationEnd, expirationInDays }: useCampaignDeadlineProps) => {

  // 등록 마감일 및 D-day 계산 로직을 useMemo로 최적화
  const { daysLeft, isClosed } = useMemo(() => {
    try {
      let endDateObj;

      const dateStr = registrationEnd;

      // 날짜 형식 처리
      // --- Try ISO format first (e.g., "2025-07-10")
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        endDateObj = parseISO(dateStr);
      }
      // --- Else try formats with dash and time (e.g., "10/07/2025-00:00:00")
      else if (dateStr.includes("-")) {
        // Try "dd/MM/yyyy-HH:mm:ss"
        endDateObj = parse(dateStr, "dd/MM/yyyy-HH:mm:ss", new Date());
        if (!isValid(endDateObj)) {
          // Try "MM/dd/yyyy-HH:mm:ss"
          endDateObj = parse(dateStr, "MM/dd/yyyy-HH:mm:ss", new Date());
        }

        // Fallback: extract date part and build manually
        if (!isValid(endDateObj)) {
          const [datePart] = dateStr.split("-");
          const [d1, d2, d3] = datePart.split("/").map(Number);
          // Try dd/MM/yyyy
          endDateObj = new Date(d3, d2 - 1, d1);
        }
      } else {
        // Last resort fallback: let Date constructor handle it
        endDateObj = new Date(dateStr);
      }

      if (!isValid(endDateObj)) throw new Error("Invalid date format");

      // Set time to 23:59:59
      const endDateWithTime = setHours(setMinutes(setSeconds(endDateObj, 59), 59), 23);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const dayDiff = differenceInDays(endDateWithTime, today);
      const closed = !isAfter(endDateWithTime, new Date());

      return {
        daysLeft: dayDiff,
        isClosed: closed,
      };
    } catch (error) {
      return {
        daysLeft: expirationInDays,
        isClosed: expirationInDays <= 0,
      };
    }
  }, [registrationEnd, expirationInDays]);

  return { daysLeft, isClosed };
};

export default useCampaignDeadline;
